import { EVENT_STATUS_LABELS, EventStatus } from '@/types/events'
import { router, usePage } from '@inertiajs/react'
import { useEffect, useState } from 'react'
import { HiChevronDown, HiChevronLeft } from 'react-icons/hi'
import { MenuItem, defaultMenuItems, findActiveMenuAndParents } from './menuItems'

interface SharedProps {
  current_user?: {
    id: number
    email: string
    name?: string
    global_admin: boolean
    event_role?: 'owner' | 'crew' | null
  }
}

interface SidebarProps {
  className?: string
  menuItems?: MenuItem[]
}

export default function Sidebar({ className = '', menuItems = defaultMenuItems }: SidebarProps) {
  const { url, props } = usePage<SharedProps>()

  // Extract event details from props
  const pageProps = props as any
  const event = pageProps?.event
  const sharedEvent = pageProps?.event_context
  const eventSlug = event?.slug || sharedEvent?.slug || url.split('/')[2]
  const eventName = event?.name || sharedEvent?.name || 'Event'
  const eventStatus: EventStatus = event?.status || sharedEvent?.status || 'draft'

  // Check if the current user is a global admin
  const currentUser = props.current_user
  const isGlobalAdmin = currentUser?.global_admin || false

  // Determine if we're inside an event-specific route (not index page)
  // Event-specific routes are like /events/:slug/... where slug is not empty
  const urlParts = url.split('/').filter(Boolean)
  const isInsideEventRoute = urlParts[0] === 'events' && urlParts.length > 1 && urlParts[1] !== ''

  // Determine if we're on a home page (events index or admin index)
  const isHomePage =
    url === '/events' || url === '/events/' || url === '/admin' || url === '/admin/'

  // Initialize state based on current URL
  const initialState = findActiveMenuAndParents(menuItems, url, eventSlug)
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(
    () => new Set(initialState.parentKeys),
  )
  const [activeMenu, setActiveMenu] = useState<string>(initialState.activeKey || 'dashboard')

  // Update active menu based on current URL (but preserve expanded menus)
  useEffect(() => {
    const result = findActiveMenuAndParents(menuItems, url, eventSlug)
    if (result.activeKey) {
      setActiveMenu(result.activeKey)
      // Only add parent keys, don't remove any expanded menus
      if (result.parentKeys.length > 0) {
        setExpandedMenus((prev) => {
          const newSet = new Set(prev)
          result.parentKeys.forEach((key) => newSet.add(key))
          return newSet
        })
      }
    }
  }, [url, menuItems, eventSlug])

  const toggleMenu = (key: string) => {
    const newExpanded = new Set(expandedMenus)
    if (newExpanded.has(key)) {
      newExpanded.delete(key)
    } else {
      newExpanded.add(key)
    }
    setExpandedMenus(newExpanded)
  }

  const handleMenuClick = (key: string, path?: string) => {
    setActiveMenu(key)
    if (path) {
      const fullPath = path.startsWith('/') ? path : `/events/${eventSlug}/${path}`
      router.visit(fullPath)
    }
  }

  const renderMenuItem = (item: MenuItem, level: number = 0) => {
    const hasChildren = item.children && item.children.length > 0
    const isExpanded = expandedMenus.has(item.key)
    const isActive = activeMenu === item.key
    const paddingLeft = level === 0 ? 'pl-4' : 'pl-16'

    return (
      <div key={item.key} className="w-full">
        <div
          className={`
            flex items-center justify-between w-full px-0 py-3 text-left
            hover:bg-ava-blue-03 transition-colors duration-150 cursor-pointer text-white
            ${isActive ? 'bg-ava-blue-03 border-r-2 border-blue-500' : ''}
            ${paddingLeft}
          `}
          onClick={() => {
            if (hasChildren) {
              toggleMenu(item.key)
            } else {
              handleMenuClick(item.key, item.path)
            }
          }}
        >
          <div className="flex items-center space-x-3">
            {item.icon && <span className="text-lg text-white">{item.icon}</span>}
            <span className="text-sm">{item.label}</span>
          </div>
          {hasChildren && (
            <span className="text-white mr-4">
              <HiChevronDown
                className={`transition-transform duration-300 text-xs ${
                  isExpanded ? 'transform rotate-180' : ''
                }`}
              />
            </span>
          )}
        </div>

        {hasChildren && isExpanded && (
          <div className="bg-gray-25 text-white">
            {item.children?.map((child) => renderMenuItem(child, level + 1))}
          </div>
        )}
      </div>
    )
  }

  return (
    <aside
      className={`
        fixed left-0 top-0 h-full w-64 bg-ava-blue-01 border-r border-gray-200
        overflow-y-auto z-10 shadow-sm ${className}
      `}
      style={{ paddingTop: '64px' }} // Account for header height
    >
      {/* Event Header - only shown inside event-specific routes */}
      {isInsideEventRoute && (
        <div className="px-4 py-6 border-b border-gray-700">
          {!isHomePage && (
            <button
              onClick={() => router.visit(isGlobalAdmin ? '/admin/events' : '/events')}
              className="flex items-center space-x-2 text-white hover:text-blue-400 transition-colors mb-8"
            >
              <HiChevronLeft className="text-sm" />
              <span className="text-sm underline">
                {isGlobalAdmin ? 'Back to Global Admin View' : 'Back to Homepage'}
              </span>
            </button>
          )}

          <h2 className="text-white text-sm mb-2">{eventName}</h2>

          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full status-indicator-${eventStatus}`} />
            <span className="text-white text-sm">{EVENT_STATUS_LABELS[eventStatus]}</span>
          </div>
        </div>
      )}

      {/* Navigation Menu */}
      <nav className="py-4">
        <div className="space-y-1">{menuItems.map((item) => renderMenuItem(item))}</div>
      </nav>
    </aside>
  )
}
